import { useState, type FormEvent } from 'react'
import { Eye, EyeOff, LoaderCircle, LockKeyhole, LogIn, UserRound } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { ApiError } from '../lib/api'

export function LoginView({ onLogin }: { onLogin: (username: string, password: string) => Promise<void> }) {
  const { t } = useTranslation()
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [pending, setPending] = useState(false)
  const [error, setError] = useState('')

  async function submit(event: FormEvent) {
    event.preventDefault()
    setPending(true)
    setError('')
    try {
      await onLogin(username, password)
    } catch (reason) {
      if (reason instanceof ApiError && reason.status === 401) {
        setError(t('auth.invalidCredentials'))
      } else if (reason instanceof ApiError && reason.status === 429) {
        setError(t('auth.rateLimited'))
      } else if (reason instanceof ApiError && reason.status >= 500) {
        setError(t('auth.serverUnavailable'))
      } else if (reason instanceof TypeError) {
        setError(t('auth.serverOffline'))
      } else {
        setError(t('auth.signInFailed'))
      }
    } finally {
      setPending(false)
    }
  }

  return <main className="login-page">
    <div className="login-scene" aria-hidden="true">
      <div className="login-scene-mark"><img src="/meine-mediathek-logo.png" alt="" /></div>
      <span>{t('auth.eyebrow')}</span>
      <strong>{t('auth.heroTitle')}</strong>
      <p>{t('auth.heroText')}</p>
      <div className="login-scene-features"><span>{t('nav.films')}</span><span>{t('nav.subscriptions')}</span><span>{t('nav.downloads')}</span></div>
    </div>
    <section className="login-card" aria-labelledby="login-title">
      <div className="login-brand">
        <img src="/meine-mediathek-logo.png" alt="" />
        <div><strong>{t('app.name')}</strong><span>{t('app.tagline')}</span></div>
      </div>
      <div className="login-copy">
        <span className="eyebrow"><LockKeyhole />{t('auth.eyebrow')}</span>
        <h1 id="login-title">{t('auth.title')}</h1>
        <p>{t('auth.subtitle')}</p>
      </div>
      <form onSubmit={submit}>
        <label className="field">
          <span>{t('auth.username')}</span>
          <div className="login-input"><UserRound /><input autoFocus autoComplete="username" required maxLength={128}
            value={username} onChange={(event) => setUsername(event.target.value)} /></div>
        </label>
        <label className="field">
          <span>{t('auth.password')}</span>
          <div className="login-input"><LockKeyhole /><input type={showPassword ? 'text' : 'password'}
            autoComplete="current-password" required maxLength={1024} value={password}
            onChange={(event) => setPassword(event.target.value)} />
            <button type="button" onClick={() => setShowPassword((visible) => !visible)}
              aria-label={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}>
              {showPassword ? <EyeOff /> : <Eye />}
            </button></div>
        </label>
        {error && <div className="inline-error" role="alert">{error}</div>}
        <button className="primary-button full-width login-submit" disabled={pending}>
          {pending ? <LoaderCircle className="spin" /> : <LogIn />}{pending ? t('auth.signingIn') : t('auth.signIn')}
        </button>
      </form>
      <small>{t('auth.securityHint')}</small>
    </section>
  </main>
}
